import { RadioChangeEvent } from 'antd/lib/radio';
import { HTMLAttributes, useEffect, useState } from 'react';
import {
  ProjectTemplateData,
  useGetAllProjectPhases,
  useGetAllTasks,
} from 'data/templates/projects';
import { queueMacrotask } from 'utils/macrotask';
import { useActiveRecord } from '../commons';

const clickElement = (selector: string) => {
  queueMacrotask(() => {
    const row = document.querySelector(selector) as HTMLElement;

    if (row) {
      row.click();
    }
  });
};
const selectRow = (key: string) => clickElement(`[data-row-key="${key}"]`);

const isMainRowElement = (element): boolean => {
  return element.classList.contains('ant-table-row');
};

export type ToggleState = 'expand' | 'collapse' | null;

export const useTableActions = (): {
  expandedRowKeys: string[];
  hasClickedExpandCollapse: boolean;
  mode: ToggleState;
  onExpand(expanded: boolean, record: ProjectTemplateData): void;
  onRowClick(record: ProjectTemplateData): HTMLAttributes<HTMLElement>;
  expandCollapse(e: RadioChangeEvent): void;
} => {
  const [mode, setMode] = useState<ToggleState>('expand');
  const allTasks = useGetAllTasks();
  const allPhases = useGetAllProjectPhases();
  const allTaskKeys = [...Object.values(allTasks)].map(
    (item) => `TASK-${item.taskId}`,
  );
  const allPhaseKeys = [...Object.values(allPhases)].map(
    (item) => `PROJECT_PHASE-${item.projectPhaseId}`,
  );
  const allExpandableKeys = [...allTaskKeys, ...allPhaseKeys];

  const activeRecord = useActiveRecord();

  const [expandedRowKeys, setExpandedRowKeys] = useState(allExpandableKeys);

  const [hasClickedExpandCollapse, setHasClickedExpandCollapse] =
    useState(false);

  useEffect(() => {
    if (activeRecord?.key) {
      selectRow(activeRecord.key);
    }
  }, [activeRecord?.key]);

  const onExpandAll = () => {
    if (expandedRowKeys.length !== allExpandableKeys.length) {
      setExpandedRowKeys(allExpandableKeys);
    }
    setHasClickedExpandCollapse(true);
  };

  const onCollapseAll = () => {
    if (expandedRowKeys.length !== 0) {
      setExpandedRowKeys([]);
    }
    setHasClickedExpandCollapse(true);
  };

  const onRowClick = () => ({
    onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (isMainRowElement(event.target)) {
        event.currentTarget.parentElement
          ?.querySelectorAll('tr')
          .forEach((row) => {
            row.classList.remove('ant-table-row-selected');
          });

        event.currentTarget.classList.add('ant-table-row-selected');
      }
    },
  });

  const onExpand = (expanded: boolean, record: ProjectTemplateData) => {
    setMode(null);

    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, record.key]);
    } else {
      setExpandedRowKeys([
        ...expandedRowKeys.filter((rowKey) => rowKey !== record.key),
      ]);
    }
  };

  const expandCollapse = (e: RadioChangeEvent) => {
    setMode(e.target.value);

    switch (e.target.value) {
      case 'expand':
        return onExpandAll();
      case 'collapse':
        return onCollapseAll();
      default:
        return null;
    }
  };

  return {
    expandedRowKeys,
    hasClickedExpandCollapse,
    mode,
    onExpand,
    onRowClick,
    expandCollapse,
  };
};

export const rowClassName = (record: ProjectTemplateData): string => {
  return record.private ? 'private' : '';
};
